// src/components/OniCard.js
// Carte Oni téléchargeable — format story Instagram (9:16) et carré (1:1)
// Générée côté client avec Canvas API, pas de dépendance serveur

import React, { useState, useRef, useEffect } from "react";
import { computeLevel } from "../data/demons";
import "./OniCard.css";

// ── Dessine la carte sur le canvas ────────────────────────────────────
async function drawOniCard(canvas, { oni, xp, displayName, format, stats }) {
  const ctx = canvas.getContext("2d");
  const W = canvas.width;
  const H = canvas.height;

  // ── Fond ─────────────────────────────────────────────────────────
  ctx.fillStyle = "#080608";
  ctx.fillRect(0, 0, W, H);

  // Halftone dots
  ctx.fillStyle = "rgba(240,235,224,0.018)";
  for (let x = 0; x < W; x += 10) {
    for (let y = 0; y < H; y += 10) {
      ctx.beginPath(); ctx.arc(x, y, 1.2, 0, Math.PI*2); ctx.fill();
    }
  }

  // Glow rouge centré
  const grad = ctx.createRadialGradient(W/2, H*0.4, 0, W/2, H*0.4, W*0.7);
  grad.addColorStop(0, "rgba(192,57,43,0.14)");
  grad.addColorStop(1, "rgba(8,6,8,0)");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, W, H);

  // Barre rouge gauche
  ctx.fillStyle = "#c0392b";
  ctx.fillRect(0, 0, 5, H);

  // Lignes décoratives
  ctx.strokeStyle = "rgba(192,57,43,0.2)";
  ctx.lineWidth = 1;
  ctx.beginPath(); ctx.moveTo(0, 80); ctx.lineTo(W, 80); ctx.stroke();
  ctx.beginPath(); ctx.moveTo(0, H-70); ctx.lineTo(W, H-70); ctx.stroke();

  // ── Header ────────────────────────────────────────────────────────
  ctx.font = `bold ${W*0.028}px monospace`;
  ctx.fillStyle = "rgba(192,57,43,0.7)";
  ctx.fillText("TAKKYU ONI · 卓球鬼", 28, 52);

  const levelInfo = computeLevel(xp || 0);
  const levelStr = `${levelInfo.current.badge} LV.${levelInfo.current.level} · ${xp || 0} XP`;
  ctx.font = `${W*0.022}px monospace`;
  ctx.fillStyle = "rgba(212,160,23,0.8)";
  ctx.textAlign = "right";
  ctx.fillText(levelStr, W-28, 52);
  ctx.textAlign = "left";

  // ── Image Oni ─────────────────────────────────────────────────────
  if (oni?.image) {
    try {
      const img = await loadImage(oni.image + "?v=2");
      const imgSize = format === "story" ? W * 0.62 : W * 0.52;
      const imgX    = (W - imgSize) / 2;
      const imgY    = format === "story" ? H * 0.12 : H * 0.1;

      // Shadow derrière l'image
      ctx.shadowColor = "rgba(192,57,43,0.5)";
      ctx.shadowBlur  = 40;
      ctx.drawImage(img, imgX, imgY, imgSize, imgSize);
      ctx.shadowBlur  = 0;

      // Dégradé fondu en bas de l'image
      const fadeGrad = ctx.createLinearGradient(0, imgY + imgSize*0.55, 0, imgY + imgSize);
      fadeGrad.addColorStop(0, "rgba(8,6,8,0)");
      fadeGrad.addColorStop(1, "rgba(8,6,8,1)");
      ctx.fillStyle = fadeGrad;
      ctx.fillRect(imgX, imgY + imgSize*0.55, imgSize, imgSize*0.45);
    } catch(e) {
      // Fallback emoji si image échoue
      ctx.font = `${W*0.35}px serif`;
      ctx.textAlign = "center";
      ctx.fillStyle = "rgba(192,57,43,0.3)";
      ctx.fillText(oni.emoji || "👹", W/2, H*0.5);
      ctx.textAlign = "left";
    }
  } else {
    // Gros emoji si pas d'image
    ctx.font = `${W*0.38}px serif`;
    ctx.textAlign = "center";
    ctx.fillStyle = "rgba(240,235,224,0.9)";
    ctx.shadowColor = "rgba(192,57,43,0.6)";
    ctx.shadowBlur  = 30;
    ctx.fillText(oni?.emoji || "👹", W/2, format==="story" ? H*0.48 : H*0.5);
    ctx.shadowBlur  = 0;
    ctx.textAlign = "left";
  }

  // ── Texte principal ───────────────────────────────────────────────
  const textY = format === "story" ? H * 0.72 : H * 0.68;

  // JP name
  ctx.font = `bold ${W*0.13}px serif`;
  ctx.fillStyle = "#c0392b";
  ctx.textAlign = "center";
  ctx.shadowColor = "rgba(192,57,43,0.4)";
  ctx.shadowBlur  = 20;
  ctx.fillText(oni?.jp || "鬼", W/2, textY);
  ctx.shadowBlur  = 0;

  // EN name
  ctx.font = `bold ${W*0.055}px monospace`;
  ctx.fillStyle = "#d4a017";
  ctx.letterSpacing = "4px";
  ctx.fillText((oni?.name || "ONI").toUpperCase(), W/2, textY + W*0.075);

  // Tagline
  if (oni?.tagline) {
    ctx.font = `italic ${W*0.033}px serif`;
    ctx.fillStyle = "rgba(240,235,224,0.55)";
    ctx.fillText(`"${oni.tagline}"`, W/2, textY + W*0.125);
  }

  // Player name
  ctx.font = `${W*0.038}px monospace`;
  ctx.fillStyle = "rgba(240,235,224,0.75)";
  ctx.fillText(displayName || "ONI", W/2, textY + W*0.175);

  ctx.textAlign = "left";

  // ── Stats top 3 (carré seulement) ────────────────────────────────
  if (format === "square" && stats) {
    const topStats = Object.entries(stats).sort(([,a],[,b])=>b-a).slice(0,3);
    const statColors = { aggression:"#c0392b", control:"#7ecec4", chaos:"#b07fe8", adaptation:"#d4a017", patience:"#4a9e6b", ego:"#e67e22", creativity:"#5b8dd9", risk:"#e74c3c", discipline:"#8a7e96", emotion:"#e91e8c" };
    const barY = H - 110;
    const barW = (W - 80) / 3;

    topStats.forEach(([key, val], i) => {
      const x = 40 + i * (barW + 10);
      const color = statColors[key] || "#8a7e96";
      const barH = (val / 99) * 50;

      // Bar
      ctx.fillStyle = `${color}22`;
      ctx.fillRect(x, barY - 50, barW - 10, 50);
      ctx.fillStyle = color;
      ctx.fillRect(x, barY - barH, barW - 10, barH);

      // Label
      ctx.font = `bold ${W*0.022}px monospace`;
      ctx.fillStyle = color;
      ctx.textAlign = "center";
      ctx.fillText(key.toUpperCase().slice(0,4), x + (barW-10)/2, barY + 16);
      ctx.fillText(val, x + (barW-10)/2, barY - 58);
    });
    ctx.textAlign = "left";
  }

  // ── Footer ────────────────────────────────────────────────────────
  ctx.font = `${W*0.022}px monospace`;
  ctx.fillStyle = "rgba(240,235,224,0.2)";
  ctx.textAlign = "center";
  ctx.fillText("takkyuoni.com", W/2, H - 30);
  ctx.textAlign = "left";
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload  = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

// ── Main component ────────────────────────────────────────────────────
export default function OniCard({ demon, xp, displayName, onClose }) {
  const [format, setFormat]     = useState("story"); // story | square
  const [rendering, setRendering] = useState(false);
  const [ready, setReady]       = useState(false);
  const canvasRef = useRef();

  const oni   = demon?.oni;
  const stats = demon?.stats;

  // Dimensions
  const DIM = {
    story:  { w: 1080, h: 1920 },
    square: { w: 1080, h: 1080 },
  };

  const render = async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    setRendering(true); setReady(false);
    const { w, h } = DIM[format];
    canvas.width  = w;
    canvas.height = h;
    await drawOniCard(canvas, { oni, xp, displayName, format, stats });
    setRendering(false); setReady(true);
  };

  useEffect(() => { render(); }, [format, oni?.id]);

  const download = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const link = document.createElement("a");
    link.download = `takkyu-oni-${oni?.id || "oni"}-${format}.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  };

  const share = async () => {
    const canvas = canvasRef.current;
    if (!canvas || !navigator.share) { download(); return; }
    canvas.toBlob(async blob => {
      try {
        await navigator.share({
          files: [new File([blob], `takkyu-oni-${oni?.id}.png`, { type:"image/png" })],
          title: `${oni?.name} · Takkyu Oni`,
          text:  `${oni?.emoji} ${displayName} — ${oni?.name}\n"${oni?.tagline}"\ntakkyuoni.com`,
        });
      } catch(e) { download(); }
    }, "image/png");
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="oni-card-modal" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="oni-card-modal-header">
          <div>
            <p className="oni-card-title">CARTE ONI</p>
            <p className="oni-card-sub">Télécharge et partage sur Instagram</p>
          </div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        {/* Format selector */}
        <div className="oni-card-formats">
          <button className={`oni-card-fmt${format==="story"?" active":""}`} onClick={()=>setFormat("story")}>
            📱 STORY (9:16)
          </button>
          <button className={`oni-card-fmt${format==="square"?" active":""}`} onClick={()=>setFormat("square")}>
            ⬛ CARRÉ (1:1)
          </button>
        </div>

        {/* Preview */}
        <div className={`oni-card-preview-wrap${format==="story"?" story":""}`}>
          {rendering && (
            <div className="oni-card-rendering">
              <span className="spinner" style={{width:24,height:24,borderWidth:3}}/>
              <p>Génération en cours...</p>
            </div>
          )}
          <canvas
            ref={canvasRef}
            className={`oni-card-canvas${ready?" ready":""}`}
            style={{display: ready ? "block" : "none"}}
          />
        </div>

        {/* Actions */}
        <div className="oni-card-actions">
          <button className="btn btn-primary" onClick={download} disabled={!ready}>
            ↓ TÉLÉCHARGER
          </button>
          {"share" in navigator && (
            <button className="btn btn-ghost" onClick={share} disabled={!ready}>
              ↗ PARTAGER
            </button>
          )}
          <button className="btn btn-ghost btn-sm" onClick={render} disabled={rendering}>
            ↺ REGÉNÉRER
          </button>
        </div>

        <p className="oni-card-tip">
          💡 Pour Instagram : télécharge → ajouter à ta story → coller l'image
        </p>
      </div>
    </div>
  );
}
